import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Stack } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { useCallback, useId, useState } from "react";

import { getEmptyTutorialStep, type TutorialSection, type TutorialStep } from "../types";
import { TutorialStepEditor } from "./TutorialStepEditor";

interface TutorialStepsSectionProps {
  title: string;
  section: TutorialSection;
  steps: TutorialStep[];
  onSaveStep: (steps: TutorialStep[]) => void;
  required?: boolean;
}

export function TutorialStepsSection(props: TutorialStepsSectionProps) {
  const { title, section, steps, onSaveStep, required = false } = props;

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const baseId = useId();

  const handleAddStep = useCallback(() => {
    setEditingIndex(steps.length);
  }, [steps.length]);

  const handleSaveStep = useCallback(
    (index: number, updatedStep: TutorialStep) => {
      const isAddingNewStep = index === steps.length;
      const newSteps = isAddingNewStep ? [...steps, updatedStep] : [...steps];

      if (!isAddingNewStep) {
        newSteps[index] = updatedStep;
      }

      setIsSaving(true);
      try {
        onSaveStep(newSteps);
        setEditingIndex(null);
      } finally {
        setIsSaving(false);
      }
    },
    [steps, onSaveStep]
  );

  const isLastStep = steps.length === 1;
  const canDeleteStep = !(required && isLastStep);

  const handleDeleteStep = useCallback(
    async (index: number) => {
      const newSteps = steps.filter((_, stepIndex) => stepIndex !== index);

      setIsSaving(true);
      try {
        onSaveStep(newSteps);
        if (editingIndex === index) {
          setEditingIndex(null);
        }
      } finally {
        setIsSaving(false);
      }
    },
    [steps, onSaveStep, editingIndex]
  );

  const handleEditStep = useCallback((index: number) => {
    setEditingIndex(index);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
  }, []);

  const handleCancelNewStep = useCallback(() => {
    setEditingIndex(null);
  }, []);

  return (
    <Box sx={{ marginBottom: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ marginBottom: 2 }}>
        <Text variant="body1">{title}</Text>
        {required && steps.length === 0 && (
          <Text variant="body2" color="warning.main">
            Required
          </Text>
        )}
      </Stack>

      {/* eslint-disable react/no-array-index-key -- tutorial steps don't have stable IDs */}
      <Stack>
        {steps.map((step, index) => (
          <TutorialStepEditorWrapper
            key={`${baseId}-${section}-step-${index}`}
            step={step}
            stepIndex={index}
            isEditing={editingIndex === index}
            isSaving={isSaving}
            canDelete={canDeleteStep}
            onSave={handleSaveStep}
            onDelete={handleDeleteStep}
            onEdit={handleEditStep}
            onCancel={handleCancelEdit}
          />
        ))}
        {editingIndex === steps.length && (
          <TutorialStepEditorWrapper
            key={`${baseId}-${section}-step-new`}
            isEditing
            canDelete
            step={getEmptyTutorialStep(section)}
            stepIndex={steps.length}
            isSaving={isSaving}
            onSave={handleSaveStep}
            onDelete={handleCancelNewStep}
            onEdit={handleEditStep}
            onCancel={handleCancelEdit}
          />
        )}
      </Stack>
      {/* eslint-enable react/no-array-index-key */}

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button variant="outlined" size="small" endIcon={<AddIcon />} onClick={handleAddStep}>
          Add step
        </Button>
      </Box>
    </Box>
  );
}

interface TutorialStepEditorWrapperProps {
  step: TutorialStep;
  stepIndex: number;
  isEditing: boolean;
  isSaving: boolean;
  canDelete: boolean;
  onSave: (index: number, step: TutorialStep) => void;
  onDelete: (index: number) => void;
  onEdit: (index: number) => void;
  onCancel: () => void;
}

function TutorialStepEditorWrapper(props: TutorialStepEditorWrapperProps) {
  const { step, stepIndex, isEditing, isSaving, canDelete, onSave, onDelete, onEdit, onCancel } =
    props;

  const handleSave = useCallback(
    (updatedStep: TutorialStep) => {
      onSave(stepIndex, updatedStep);
    },
    [onSave, stepIndex]
  );

  const handleDelete = useCallback(() => {
    onDelete(stepIndex);
  }, [onDelete, stepIndex]);

  const handleEdit = useCallback(() => {
    onEdit(stepIndex);
  }, [onEdit, stepIndex]);

  return (
    <TutorialStepEditor
      step={step}
      stepIndex={stepIndex}
      isEditing={isEditing}
      isSaving={isSaving}
      canDelete={canDelete}
      onSave={handleSave}
      onDelete={handleDelete}
      onEdit={handleEdit}
      onCancel={onCancel}
    />
  );
}
